import React, {
  createContext, useContext, useState, useEffect, useCallback, useRef,
} from 'react';
import { getActiveSurveys, submitSurveyResponse } from '../services/api/survey.service';
import type { Survey, SurveyAnswers } from '../types/survey';

interface SurveyContextValue {
  /** Latest active survey the user can take (or null). */
  survey: Survey | null;
  /** True when there is an active survey to take (drives the navbar button visibility). */
  hasActiveSurvey: boolean;
  loading: boolean;
  isOpen: boolean;
  openSurvey: () => void;
  /** Close the popup. `markSeen` records a per-session dismissal so it won't auto-reopen. */
  closeSurvey: (markSeen?: boolean) => void;
  submitResponse: (answers: SurveyAnswers) => Promise<void>;
  refresh: () => Promise<void>;
}

const SurveyContext = createContext<SurveyContextValue | undefined>(undefined);

const seenKey = (id: number) => `nova_survey_seen_${id}`;
const wasSeenThisSession = (id: number) => {
  try { return sessionStorage.getItem(seenKey(id)) === '1'; } catch { return false; }
};
const markSeenThisSession = (id: number) => {
  try { sessionStorage.setItem(seenKey(id), '1'); } catch { /* ignore */ }
};

export const SurveyProvider: React.FC<{
  isAuthenticated: boolean;
  children: React.ReactNode;
}> = ({ isAuthenticated, children }) => {
  const [survey, setSurvey] = useState<Survey | null>(null);
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const autoOpenedFor = useRef<number | null>(null);

  const refresh = useCallback(async () => {
    if (!isAuthenticated) {
      setSurvey(null);
      return;
    }
    try {
      setLoading(true);
      const surveys = await getActiveSurveys();
      // Backend returns active surveys (already filtered to unanswered) newest-first.
      setSurvey(surveys.length > 0 ? surveys[0] : null);
    } catch {
      // Non-fatal: surveys are optional. Stay silent so we never block the app.
      setSurvey(null);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  // Fetch once authenticated; clear when logged out.
  useEffect(() => {
    if (isAuthenticated) {
      refresh();
    } else {
      setSurvey(null);
      setIsOpen(false);
      autoOpenedFor.current = null;
    }
  }, [isAuthenticated, refresh]);

  // Auto-open once per session for each survey the user hasn't seen yet.
  useEffect(() => {
    if (!survey) return;
    if (autoOpenedFor.current === survey.id) return;
    if (!wasSeenThisSession(survey.id)) {
      autoOpenedFor.current = survey.id;
      setIsOpen(true);
    }
  }, [survey]);

  const openSurvey = useCallback(() => setIsOpen(true), []);

  const closeSurvey = useCallback((markSeen = true) => {
    setIsOpen(false);
    if (markSeen && survey) markSeenThisSession(survey.id);
  }, [survey]);

  const submitResponse = useCallback(async (answers: SurveyAnswers) => {
    if (!survey) return;
    await submitSurveyResponse(survey.id, answers);
    markSeenThisSession(survey.id);
    setIsOpen(false);
    await refresh(); // answered survey drops out of the active list -> button hides
  }, [survey, refresh]);

  const value: SurveyContextValue = {
    survey,
    hasActiveSurvey: !!survey,
    loading,
    isOpen,
    openSurvey,
    closeSurvey,
    submitResponse,
    refresh,
  };

  return <SurveyContext.Provider value={value}>{children}</SurveyContext.Provider>;
};

export const useSurvey = (): SurveyContextValue => {
  const ctx = useContext(SurveyContext);
  if (!ctx) throw new Error('useSurvey must be used within a SurveyProvider');
  return ctx;
};
